import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Eye, Check, X, Upload, Download, Wallet, MessageSquare } from "lucide-react";
import B2BChat from "@/components/B2BChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Row background tint per B2B status (subtle — badge remains the primary indicator).
const ROW_TINT = {
  "Pending Approval": "bg-yellow-50/60 hover:bg-yellow-50",
  Approved: "bg-green-50/50 hover:bg-green-50",
  Rejected: "bg-red-50/50 hover:bg-red-50",
  "Invoice Uploaded": "bg-blue-50/50 hover:bg-blue-50",
  "Edit Requested": "bg-amber-50/60 hover:bg-amber-50",
  "Info Requested": "bg-orange-50/60 hover:bg-orange-50",
};

export default function B2BSubmissions() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [subs, setSubs] = useState([]);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [accountantRemark, setAccountantRemark] = useState("");
  const [sendingRemark, setSendingRemark] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ invoice_number: "", invoice_amount: "", file: null, invoice_type: "Tax" });
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [tab, setTab] = useState("All");
  const [stats, setStats] = useState({ total: 0, by_status: {} });

  const TABS = [
    { key: "All", label: "All", status: null },
    { key: "Pending Approval", label: "Pending Approval", status: "Pending Approval" },
    { key: "Approved", label: "Approved (needs invoice)", status: "Approved" },
    { key: "Invoice Uploaded", label: "Invoice Uploaded", status: "Invoice Uploaded" },
    { key: "Pending Payment", label: "Pending Payment", status: "Pending Payment" },
    { key: "Edit Requested", label: "Edit Requested", status: "Edit Requested" },
    { key: "Info Requested", label: "Info Requested", status: "Info Requested" },
    { key: "Rejected", label: "Rejected", status: "Rejected" },
  ];

  const loadReqRef = React.useRef(0);
  const load = useCallback(async (activeTab = tab) => {
    // Stale-result guard: rapid tab switches can otherwise let an older
    // response overwrite a newer one. We tag every request with a monotonic
    // id and only apply the response if it's still the latest.
    const reqId = ++loadReqRef.current;
    try {
      const activeCfg = TABS.find((t) => t.key === activeTab) || TABS[0];
      const q = activeCfg.status ? `?status=${encodeURIComponent(activeCfg.status)}` : "";
      const [subsRes, statsRes] = await Promise.all([
        api.get(`/b2b/submissions${q}`),
        api.get("/b2b/submissions/stats"),
      ]);
      if (reqId !== loadReqRef.current) return; // superseded
      setSubs(subsRes.data);
      setStats(statsRes.data || { total: 0, by_status: {} });
    } catch (e) {
      if (reqId !== loadReqRef.current) return;
      toast.error(formatApiError(e));
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (s) => {
    setDetail(s);
    setRemarks(s.admin_remarks || "");
    setAccountantRemark("");
    setInvoiceForm({ invoice_number: s.invoice_number || "", invoice_amount: s.invoice_amount || "", file: null, invoice_type: s.invoice_type || "Tax" });
  };

  const decide = async (status, overrideRemarks) => {
    try {
      const admin_remarks = overrideRemarks !== undefined ? overrideRemarks : remarks;
      await api.put(`/b2b/submissions/${detail.id}/decision`, { status, admin_remarks });
      toast.success(`Marked ${status}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadInvoice = async () => {
    const { invoice_number, invoice_amount, file, invoice_type } = invoiceForm;
    if (!file) { toast.error("Choose a PDF file"); return; }
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF allowed"); return; }
    if (!invoice_number.trim()) { toast.error("Invoice number required"); return; }
    const amt = Number(invoice_amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Invoice amount must be > 0"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("invoice_number", invoice_number.trim());
    fd.append("invoice_amount", String(amt));
    fd.append("invoice_type", invoice_type || "Tax");
    setUploadingInvoice(true);
    try {
      await api.post(`/b2b/submissions/${detail.id}/invoice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${invoice_type} invoice uploaded`);
      setDetail(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingInvoice(false); }
  };

  const download = (ref) => window.open(`${API}/b2b/invoice/${encodeURIComponent(ref)}`, "_blank");
  const viewPayment = (subId) => window.open(`${API}/b2b/payment/${subId}/view`, "_blank");

  const markPaid = async () => {
    if (!window.confirm("Mark this payment as received?")) return;
    try {
      await api.put(`/b2b/submissions/${detail.id}/payment`);
      toast.success("Payment marked as received");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendAccountantRemark = async () => {
    const r = accountantRemark.trim();
    if (r.length < 3) { toast.error("Please describe the issue"); return; }
    setSendingRemark(true);
    try {
      await api.post(`/b2b/submissions/${detail.id}/accountant-remark`, { remark: r });
      toast.success("Remark sent — party will see it on the tracker");
      setDetail(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingRemark(false); }
  };

  const filtered = subs.filter((s) =>
    [s.reference_number, s.ibo_id, s.ibo_name, s.party_name, s.party_phone, s.invoice_number, s.party_gst]
      .join(" ").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">
            {isAdmin ? "B2B Submissions" : "B2B Invoicing"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? "Review pending submissions and approve or reject."
              : "Upload tax invoices for approved submissions."}
          </p>
        </div>
        {isAdmin && (
          <Button asChild data-testid="admin-new-b2b-bill">
            <Link to="/b2b/my-bills">
              <Plus className="w-4 h-4 mr-1" /> New Bill
            </Link>
          </Button>
        )}
      </div>

      <Card className="p-4 space-y-4">
        <Input
          placeholder="Search ref, IBO, party, phone, GST, invoice #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          data-testid="b2b-search"
        />
        {/* Category tabs — filter by status. Counts come from /b2b/submissions/stats
            so the accountant/admin can quickly see how many bills need each action
            without loading every submission. */}
        <div className="flex flex-wrap gap-2" data-testid="b2b-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const count = t.status ? (stats.by_status?.[t.status] ?? 0) : stats.total;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`b2b-tab-${t.key.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white text-foreground border-border hover:bg-muted"
                }`}
              >
                <span>{t.label}</span>
                <span className={`inline-block min-w-[1.5rem] text-center rounded-full px-1.5 text-[10px] font-semibold ${
                  active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-foreground"
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>IBO</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-8">No submissions</TableCell></TableRow>
              )}
              {filtered.map((s) => (
                <TableRow
                  key={s.id}
                  data-testid={`b2b-row-${s.reference_number}`}
                  className={ROW_TINT[s.status] || ""}
                >
                  <TableCell className="font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      <span>{s.reference_number}</span>
                      {s.unread_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-700 text-[10px] font-semibold"
                          title={`${s.unread_count} unread message${s.unread_count === 1 ? "" : "s"}`}
                          data-testid={`b2b-unread-${s.reference_number}`}
                        >
                          <MessageSquare className="w-3 h-3" />
                          {s.unread_count}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-xs">{s.ibo_name} <span className="text-muted-foreground">({s.ibo_id})</span></TableCell>
                  <TableCell className="text-xs font-medium">{s.party_name}</TableCell>
                  <TableCell className="text-xs">{s.party_phone}</TableCell>
                  <TableCell className="text-xs">{(s.items || []).reduce((n, i) => n + Number(i.quantity || 0), 0)}</TableCell>
                  <TableCell className="text-xs font-mono">{s.invoice_number || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs font-medium whitespace-nowrap">{Number(s.invoice_amount || 0) > 0 ? `₹${Number(s.invoice_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs">
                    <span
                      className={`px-2 py-0.5 rounded-full border ${s.payment_status === "Received" ? "bg-green-50 border-green-300 text-green-700" : s.payment_screenshot ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-muted border-border text-muted-foreground"}`}
                      data-testid={`b2b-payment-badge-${s.reference_number}`}
                    >
                      {s.payment_status === "Received" ? "Received" : s.payment_screenshot ? "Pending verify" : "Not uploaded"}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(s)} data-testid={`b2b-view-${s.reference_number}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {s.tax_invoice_file && (
                      <Button variant="ghost" size="sm" onClick={() => download(s.reference_number)} data-testid={`b2b-download-${s.reference_number}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); load(); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Submission {detail.reference_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><div className="text-xs text-muted-foreground">Status</div><StatusBadge status={detail.status} /></div>
                  <div><div className="text-xs text-muted-foreground">Created</div><div>{new Date(detail.created_at).toLocaleString()}</div></div>
                  <div><div className="text-xs text-muted-foreground">IBO ID</div><div>{detail.ibo_id}</div></div>
                  <div><div className="text-xs text-muted-foreground">IBO Name</div><div>{detail.ibo_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Party Name</div><div className="font-medium">{detail.party_name}</div></div>
                  <div><div className="text-xs text-muted-foreground">Party Phone</div><div>{detail.party_phone}</div></div>
                  <div className="col-span-2"><div className="text-xs text-muted-foreground">Party GST</div><div>{detail.party_gst || "—"}</div></div>
                  <div className="col-span-2"><div className="text-xs text-muted-foreground">Billing Address</div><div className="whitespace-pre-wrap">{detail.party_billing_address}</div></div>
                  <div className="col-span-2"><div className="text-xs text-muted-foreground">Shipping Address</div><div className="whitespace-pre-wrap">{detail.party_shipping_address}</div></div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Products</div>
                  <div className="border border-border rounded-md divide-y divide-border">
                    {(detail.items || []).map((it) => (
                      <div key={it.sku_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span>{it.sku_name}</span>
                        <span className="font-medium">× {it.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {detail.notes && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Party notes</div>
                    <div className="text-sm bg-muted/40 border border-border rounded-md p-3 whitespace-pre-wrap">{detail.notes}</div>
                  </div>
                )}

                {detail.status === "Edit Requested" && detail.edit_reason && (
                  <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm">
                    <div className="text-xs text-amber-700 font-medium mb-1">Edit requested — re-issue invoice</div>
                    <div className="whitespace-pre-wrap">{detail.edit_reason}</div>
                  </div>
                )}

                {/* Chat panel — anyone with access (admin, accountant, owning IBO)
                    can message on this bill at any lifecycle stage. */}
                <B2BChat submissionId={detail.id} currentUserId={user.id} />
                {isAdmin && detail.status === "Pending Approval" && (
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground">Admin remarks (optional)</label>
                    <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="b2b-admin-remarks" />
                  </div>
                )}
                {detail.admin_remarks && detail.status !== "Pending Approval" && (
                  <div className="bg-muted/40 border border-border rounded-md p-3 text-sm">
                    <div className="text-xs text-muted-foreground mb-1">Admin remarks</div>
                    {detail.admin_remarks}
                  </div>
                )}

                {detail.accountant_remark && (
                  <div className="bg-orange-50 border border-orange-300 rounded-md p-3 text-sm" data-testid="b2b-accountant-remark-display">
                    <div className="text-xs text-orange-700 font-medium mb-1">
                      Accountant remark {detail.accountant_remark_at ? `· ${new Date(detail.accountant_remark_at).toLocaleString()}` : ""}
                    </div>
                    <div className="whitespace-pre-wrap">{detail.accountant_remark}</div>
                  </div>
                )}

                {(detail.status === "Approved" || detail.status === "Info Requested") && (
                  <div className="space-y-2 border-t border-border pt-4">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {detail.status === "Info Requested" ? "Send another remark" : "Send remark to party"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      If something is wrong before you generate the tax invoice, describe the issue.
                      The party will see this on the tracker and can edit their submission.
                    </p>
                    <Textarea
                      rows={2}
                      value={accountantRemark}
                      onChange={(e) => setAccountantRemark(e.target.value)}
                      placeholder="e.g. Party GST looks incorrect, please confirm"
                      data-testid="b2b-accountant-remark-input"
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={sendAccountantRemark}
                        disabled={sendingRemark}
                        data-testid="b2b-send-accountant-remark-btn"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {sendingRemark ? "Sending..." : "Send Remark"}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Payment</span>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${detail.payment_status === "Received" ? "bg-green-50 border-green-300 text-green-700" : detail.payment_screenshot ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-muted border-border text-muted-foreground"}`}
                    >
                      {detail.payment_status === "Received" ? "Received" : detail.payment_screenshot ? "Pending verify" : "Not uploaded"}
                    </span>
                  </div>
                  {detail.payment_uploaded_at && (
                    <div className="text-xs text-muted-foreground">Screenshot uploaded {new Date(detail.payment_uploaded_at).toLocaleString()}</div>
                  )}
                  {detail.payment_received_at && (
                    <div className="text-xs text-muted-foreground">Received on {new Date(detail.payment_received_at).toLocaleString()}</div>
                  )}
                </div>

                {detail.tax_invoice_file && (
                  <div className="border-t border-border pt-4 grid grid-cols-2 gap-4 text-sm" data-testid="b2b-invoice-info">
                    <div><div className="text-xs text-muted-foreground">Invoice Type</div><div className="font-medium">{detail.invoice_type || "Tax"}</div></div>
                    <div><div className="text-xs text-muted-foreground">Invoice Number</div><div className="font-mono">{detail.invoice_number || "—"}</div></div>
                    <div><div className="text-xs text-muted-foreground">Invoice Amount</div><div className="font-semibold">₹{Number(detail.invoice_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
                    {detail.tax_invoice_uploaded_at && <div className="col-span-2 text-xs text-muted-foreground">Uploaded {new Date(detail.tax_invoice_uploaded_at).toLocaleString()}</div>}
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                {isAdmin && detail.status === "Pending Approval" && (
                  <>
                    <Button variant="destructive" onClick={() => decide("Rejected")} data-testid="b2b-reject-btn">
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button onClick={() => decide("Approved")} data-testid="b2b-approve-btn">
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </>
                )}
                {isAdmin && detail.status !== "Pending Approval" && detail.status !== "Rejected" && (
                  // Admin override — cancel a bill mid-lifecycle. Prompts for a
                  // reason so downstream teams (accountant, IBO) see why.
                  <Button
                    variant="outline"
                    onClick={() => {
                      const reason = window.prompt(
                        `Reject this ${detail.status} bill?\n\nOptional: enter a reason that the IBO / accountant will see.`,
                        detail.admin_remarks || ""
                      );
                      if (reason === null) return; // cancelled
                      decide("Rejected", reason);
                    }}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="b2b-force-reject-btn"
                  >
                    <X className="w-4 h-4 mr-1" /> Reject Bill
                  </Button>
                )}
                {!isAdmin && (detail.status === "Approved" || detail.status === "Edit Requested" || detail.status === "Invoice Uploaded") && (
                  <div className="w-full space-y-3 border-t border-border pt-4">
                    <div className="flex gap-2 items-center">
                      <label className="text-xs text-muted-foreground">Invoice type:</label>
                      <div className="flex gap-2">
                        {["Proforma", "Tax"].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setInvoiceForm({ ...invoiceForm, invoice_type: t })}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${invoiceForm.invoice_type === t ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
                            data-testid={`b2b-invoice-type-${t.toLowerCase()}`}
                          >
                            {t} Invoice
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Invoice Number *</label>
                        <Input
                          value={invoiceForm.invoice_number}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                          placeholder={invoiceForm.invoice_type === "Proforma" ? "e.g. PI/2026/0042" : "e.g. INV/2026/0042"}
                          data-testid="b2b-invoice-number"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Invoice Amount (₹) *</label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={invoiceForm.invoice_amount}
                          onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_amount: e.target.value })}
                          data-testid="b2b-invoice-amount"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        {invoiceForm.invoice_type} Invoice (PDF) *
                        {detail.status === "Invoice Uploaded" && detail.invoice_type && detail.invoice_type !== invoiceForm.invoice_type && (
                          <span className="ml-2 text-amber-600">Will replace the existing {detail.invoice_type} invoice.</span>
                        )}
                      </label>
                      <Input
                        type="file" accept="application/pdf"
                        onChange={(e) => setInvoiceForm({ ...invoiceForm, file: e.target.files?.[0] || null })}
                        data-testid="b2b-invoice-file-input"
                      />
                    </div>
                    <Button onClick={uploadInvoice} disabled={uploadingInvoice} data-testid="b2b-upload-invoice-btn">
                      <Upload className="w-4 h-4 mr-1" /> {uploadingInvoice ? "Uploading..." : `Upload ${invoiceForm.invoice_type} Invoice`}
                    </Button>
                  </div>
                )}
                {detail.tax_invoice_file && (
                  <Button variant="outline" onClick={() => download(detail.reference_number)}>
                    <Download className="w-4 h-4 mr-1" /> Download Invoice
                  </Button>
                )}
                {detail.payment_screenshot && (
                  <Button variant="outline" onClick={() => viewPayment(detail.id)} data-testid="b2b-view-payment-btn">
                    <Eye className="w-4 h-4 mr-1" /> View Payment
                  </Button>
                )}
                {detail.payment_status !== "Received" && (
                  <Button onClick={markPaid} data-testid="b2b-mark-paid-btn">
                    <Wallet className="w-4 h-4 mr-1" /> Mark Payment Received
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
